'use client';

import CustomQuillEditor from '@/components/custom-quill-editor';
import CustomToast from '@/components/custom-toast';
import { template_default } from '@/components/custom-toast/sonner-with-template';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { useEmployeeInfoStore } from '@/hooks/use-employee-store';
import { cn } from '@/lib/utils';
import { DossierNotificationResponse } from '@/server/api/v1/dossier-notification/type';
import { AppNotificationResponse } from '@/server/api/v1/notification/type';
import {
    SignalMethod,
    SignalNotificationType,
    SocketEndpoint,
} from '@/signalR/enum';
import { useSocketListener } from '@/signalR/use-socket-listener';
import { api } from '@/trpc/client';
import { Bell } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import DossierNotification from './tab-dossier/dossier-notifications';
import {
    useAppNotificationStore,
    useDossierNotificationStore,
    useHeaderNotificationDropdownStore,
} from './utils';

const { sonner } = CustomToast;

type Props = {
    className?: string;
    /**Đây là header-notifications dành cho khách hàng */
    isForCustomer?: boolean;
};

function HeaderNotification({ className, isForCustomer = false }: Props) {
    /**Chặn load lần đầu — chỉ fetch sau khi người dùng mở panel */
    const _isFetchedRef = useRef(false);

    const apiUtils = api.useUtils();
    const empData = useEmployeeInfoStore((x) => x.current);

    const {
        totalUnRead: totalUnRead_dossierNotice,
        setStoreState: setStore_dossierNotice,
    } = useDossierNotificationStore((x) => x);

    const { setStoreState: setStore_appNotice } = useAppNotificationStore(
        (x) => x
    );

    const { open, setOpen, setIsCustomer } = useHeaderNotificationDropdownStore(
        (x) => x
    );

    const onReloadAllNotice = useCallback(() => {
        apiUtils.appNotification.invalidate();
        apiUtils.dossierNotification.invalidate();
    }, [apiUtils.appNotification, apiUtils.dossierNotification]);

    const callbackSendNotification = useCallback(
        ({
            Type,
            Data,
        }: {
            Type: SignalNotificationType;
            Data: AppNotificationResponse[];
        }) => {
            if (Type === SignalNotificationType.Create && Data.length) {
                for (let x of Data) {
                    if (x.TargetID === empData?.ID) {
                        sonner.info(
                            template_default({
                                title: x.Title ?? 'Bạn có thông báo mới',
                                desc: x.Value ? (
                                    <CustomQuillEditor.RenderContent
                                        content={x.Value}
                                    />
                                ) : (
                                    ''
                                ),
                            }),
                            { duration: 5000, position: 'top-right' }
                        );
                    }
                }
                onReloadAllNotice();
            }
        },
        [empData?.ID, onReloadAllNotice]
    );

    const callbackSendDossierNotification = useCallback(
        ({
            Type,
            Data,
        }: {
            Type: SignalNotificationType;
            Data: DossierNotificationResponse[];
        }) => {
            if (Type === SignalNotificationType.Create && Data.length) {
                for (let x of Data) {
                    sonner.info(
                        template_default({
                            title: 'Thông báo: ' + (x.Title ?? ''),
                            desc: x.Value ? (
                                <CustomQuillEditor.RenderContent
                                    content={x.Value}
                                />
                            ) : (
                                ''
                            ),
                        }),
                        { duration: 5000, position: 'top-right' }
                    );
                }
                onReloadAllNotice();
            }
        },
        [onReloadAllNotice]
    );

    /**Cập nhật số lượng chưa đọc ngay sau khi SignalR kết nối thành công */
    const callbackSendAfterConnect = useCallback(
        (data: { Notifications: number; DossierNotifications: number }) => {
            setStore_dossierNotice({ totalUnRead: data.DossierNotifications });
            setStore_appNotice({ totalUnRead: data.Notifications });
        },
        [setStore_appNotice, setStore_dossierNotice]
    );

    useSocketListener({
        endPoint: SocketEndpoint.hubs,
        listener: {
            [SignalMethod.SendAfterConnect]: callbackSendAfterConnect,
            [SignalMethod.SendNotification]: callbackSendNotification,
            [SignalMethod.SendDossierNotification]:
                callbackSendDossierNotification,
        },
    });

    useEffect(() => {
        setIsCustomer(isForCustomer);
    }, [isForCustomer, setIsCustomer]);

    // TODO: khi bật lại app-notifications thì đổi về: totalUnRead_appNotice + totalUnRead_dossierNotice
    const totalUnRead = totalUnRead_dossierNotice;

    return (
        <Popover
            open={open}
            onOpenChange={(val) => {
                if (val) _isFetchedRef.current = true;
                setOpen(val);
            }}
        >
            <PopoverTrigger asChild>
                <Button
                    variant='secondary'
                    size='icon'
                    className={cn('relative rounded-full', className)}
                >
                    <Bell className='h-5 w-5' strokeWidth={2} />
                    <span className='sr-only'>Thông báo</span>

                    {totalUnRead > 0 && (
                        <Badge
                            variant='destructive'
                            className='absolute -right-3 -top-2 flex h-5 w-5 items-center justify-center p-0 text-[10px]'
                        >
                            {totalUnRead > 10 ? '10+' : totalUnRead}
                        </Badge>
                    )}
                </Button>
            </PopoverTrigger>

            <PopoverContent
                className={cn(
                    'w-screen max-w-none overflow-hidden rounded-xl p-0 shadow-lg sm:max-w-[440px]',
                    /* Animation mượt theo data-state của Radix */
                    'data-[state=open]:animate-in data-[state=closed]:animate-out',
                    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                    'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                    'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2'
                )}
                align='end'
            >
                <DossierNotification enableAPI={_isFetchedRef.current} />
            </PopoverContent>
        </Popover>
    );
}

export default HeaderNotification;
